import { FillStyle, type Style } from '@/domain/model';

/**
 * Where a wash goes.
 *
 * ## The failure this exists to end
 *
 * A figure of "hình chóp S.ABCD, đáy hình vuông tâm O" came back with **D missing** — no point,
 * no label, nothing. It had not failed to draw: the shaded plane `(SAC)`, written later in the
 * script and therefore painted later, is a large opaque triangle and D was underneath it. The
 * script was correct, every check passed, and the figure was unreadable.
 *
 * ## Why the author's order is not the answer
 *
 * Z-order is the author's tool for deciding which of two *lines* is on top, and it should stay
 * that. But a filled region is not a line — it is a background, and no reader has ever wanted
 * one in front of the geometry it explains. Asking a teacher to remember to write their planes
 * first is asking them to know the paint algorithm, and the one thing they will forget is the
 * one that loses a vertex.
 *
 * So the rule is applied here rather than left to the script: regions first, in the order they
 * were written, then everything else, in the order it was written. Relative order *within* each
 * group is untouched, so an author who deliberately laid one wash over another still gets it.
 *
 * ## Why it lives in `rendering` and not in the document
 *
 * Because it is a drawing convention, not a fact about the figure — and because the screen and
 * the page have to agree. The canvas renderer walks the document itself while the exporters walk
 * a draw list; both call this, so a printed worksheet cannot come out layered differently from
 * the screen it was checked on.
 */

/** Whether this style paints a region rather than a line. */
export function isWash(style: Style): boolean {
  return style.fillStyle !== FillStyle.None && style.fill !== 'transparent';
}

/**
 * The same items, with the washes moved to the front.
 *
 * Stable on both sides of the partition, which is what makes it safe to apply to an order the
 * author chose: nothing is reordered except across the one boundary.
 */
export function washesFirst<T>(items: readonly T[], styleOf: (item: T) => Style): readonly T[] {
  const washes: T[] = [];
  const lines: T[] = [];
  for (const item of items) (isWash(styleOf(item)) ? washes : lines).push(item);

  // Nothing filled: hand back the original array so the common case allocates nothing.
  return washes.length === 0 ? items : [...washes, ...lines];
}
